<template>
  <div>
    <div>
      <div class="col form-group mt-3">
        <input
          v-model="obj.birthDate"
          v-mask="'##/##/####'"
          class="w-100"
          :class="template.secondaryTheme ? 'secondary-input-theme' : ' c-input'"
          :placeholder="translation.birthDate"
          @keyup.enter="validateFields"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { mask } from 'vue-the-mask'
import { getBirthDateMinimumAge } from '@/helpers/registrationAge'

export default {
  name: 'BirthDate',
  props: {
    validate: Boolean,
  },
  mask: {
    ...mask,
    tokens: {
      ...mask.tokens,
      null: /./,
    },
  },
  data: () => ({
    obj: {},
    toastMessage: '',
  }),
  computed: {
    ...mapState(['config', 'registrationObject', 'subdomain', 'template']),
    minimumAgeForValidation() {
      return getBirthDateMinimumAge(this.config)
    },
    translation() {
      return {
        attention: this.$t('common.attention'),
        birthDate: this.$t('birthDate.placeholder'),
        alertBirthDate: this.$t('birthDate.required'),
        alertValidDate: this.$t('birthDate.invalid'),
        alertMinimumAgeToAccount: this.$t('birthDate.minimumAge', {
          age: this.minimumAgeForValidation,
        }),
      }
    },
  },
  watch: {
    validate(newValue) {
      if (newValue) this.validateFields()
    },
  },
  mounted() {
    // Creates a deep copy of the object in the store
    this.obj = JSON.parse(JSON.stringify(this.registrationObject))
  },
  methods: {
    ...mapMutations([
      'updateRegistrationObject',
      'clearRegistrationErrors',
      'addRegistrationError',
    ]),
    makeToast(variant = null) {
      this.$bvToast.toast(this.toastMessage, {
        title: this.translation.attention,
        variant: variant,
        solid: true,
      })
    },
    validateBirthDate(dateStr, clientMinimumAge = 0) {
      // First check for the pattern
      if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) return false

      // Parse the date parts to integers
      let parts = dateStr.split('/')

      let day = parseInt(parts[0], 10)
      let month = parseInt(parts[1], 10)
      let year = parseInt(parts[2], 10)

      // Check the ranges of month and year
      if (year < 1000 || year > 3000 || month == 0 || month > 12) return false

      const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

      // Adjust for leap years
      if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
        monthLength[1] = 29
      }

      // Check the range of the day
      if (day <= 0 || day > monthLength[month - 1]) return false

      // Convert the input date to a Date object
      const inputDate = new Date(year, month - 1, day)
      const today = new Date()

      // Calculate the maximum birthdate (150 years ago)
      const maxBirthDate = new Date(today)
      maxBirthDate.setFullYear(today.getFullYear() - 150)

      // Calculate the minimum birthdate based on the client age configuration
      const minBirthDate = new Date(today)
      if (clientMinimumAge > 0) {
        minBirthDate.setFullYear(today.getFullYear() - clientMinimumAge)
      }

      // Validate that the input date is within the allowed range
      if (inputDate < maxBirthDate || inputDate > minBirthDate) {
        return false
      }

      return true
    },
    validateFields() {
      this.$emit('validating')

      this.clearRegistrationErrors()

      let hasErrors = false

      if (!this.obj.birthDate || this.obj.birthDate.length < 10) {
        hasErrors = true

        this.addRegistrationError({
          msg: this.translation.alertBirthDate,
        })
        this.toastMessage = this.translation.alertBirthDate
        this.makeToast('warning')
      }

      const mustValidateMinimumAge = this.minimumAgeForValidation > 0
      const isDateValid = this.validateBirthDate(this.obj.birthDate, 0)
      const meetsMinimumAge = this.validateBirthDate(
        this.obj.birthDate,
        this.minimumAgeForValidation,
      )

      if (!isDateValid) {
        hasErrors = true
        this.addRegistrationError({
          msg: this.translation.alertValidDate,
        })
        this.toastMessage = this.translation.alertValidDate
        this.makeToast('warning')
      } else if (mustValidateMinimumAge && !meetsMinimumAge) {
        hasErrors = true
        this.addRegistrationError({
          msg: this.translation.alertMinimumAgeToAccount,
        })
        this.toastMessage = this.translation.alertMinimumAgeToAccount
        this.makeToast('warning')
      }

      if (!hasErrors) {
        this.$store.commit('updateRegistrationObject', this.obj)

        this.$emit('next')
      }
    },
  },
}
</script>

<style scoped></style>
